import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Job, Applicant, initialJobs } from '../pages/types/jobs'

interface JobsContextType {
  jobs: Job[]
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  getJobById: (id: string) => Job | undefined
  updateJob: (id: string, updates: Partial<Job>) => void
  updateApplicantStatus: (
    jobId: string,
    applicantId: string,
    status: Applicant['status']
  ) => void
}

const JobsContext = createContext<JobsContextType | undefined>(undefined)

export const useJobs = () => {
  const context = useContext(JobsContext)
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobsProvider')
  }
  return context
}

interface JobsProviderProps {
  children: ReactNode
}

export const JobsProvider: React.FC<JobsProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Job[]>(initialJobs)

  const getJobById = (id: string) => {
    return jobs.find(job => job.id === id)
  }

  const updateJob = (id: string, updates: Partial<Job>) => {
    setJobs(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              ...updates,
              updatedAt: new Date().toISOString().split('T')[0],
            }
          : job
      )
    )
  }

  const updateApplicantStatus = (
    jobId: string,
    applicantId: string,
    status: Applicant['status']
  ) => {
    setJobs(prev =>
      prev.map(job => {
        if (job.id === jobId) {
          const updatedApplicants = job.applicants.map(applicant =>
            applicant.id === applicantId ? { ...applicant, status } : applicant
          )
          return {
            ...job,
            applicants: updatedApplicants,
            updatedAt: new Date().toISOString().split('T')[0],
          }
        }
        return job
      })
    )
  }

  const value = {
    jobs,
    setJobs,
    getJobById,
    updateJob,
    updateApplicantStatus,
  }

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>
}
